import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import CustomTextField from "../../../../../components/common/Textfield";
import { Permission } from "../../types/permissions";
import PermissionAppList from "../../PermissionAppList";
import { usePermissionsPicker } from "../../usePermissionsPicker";

interface RolePermissionsStepProps {
  allPermissions: Permission[];
  granted: Set<number>;
  setGranted: React.Dispatch<React.SetStateAction<Set<number>>>;
}

const RolePermissionsStep: React.FC<RolePermissionsStepProps> = ({
  allPermissions,
  granted,
  setGranted,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const {
    searchTerm,
    setSearchTerm,
    expandedApps,
    toggleApp,
    visibleGrouped,
    togglePermission,
    toggleAllPermissionsForApp,
    toggleFullCrudForModel,
    isAppFullyGranted,
    isAppPartiallyGranted,
    handleBulk,
  } = usePermissionsPicker(allPermissions, granted, setGranted);

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={0.5}>
        Choose permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Select which apps and models this role can access.
      </Typography>

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
        <CustomTextField
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        <Chip label={`${granted.size} Selected`} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleBulk("selectAll")}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleBulk("clearAll")}
        >
          Clear All
        </Button>
      </Box>

      <PermissionAppList
        visibleGrouped={visibleGrouped}
        granted={granted}
        expandedApps={expandedApps}
        toggleApp={toggleApp}
        toggleAllPermissionsForApp={toggleAllPermissionsForApp}
        toggleFullCrudForModel={toggleFullCrudForModel}
        togglePermission={togglePermission}
        isAppFullyGranted={isAppFullyGranted}
        isAppPartiallyGranted={isAppPartiallyGranted}
        accentColor={reduxTheme.primary_colors[400]}
      />
    </Box>
  );
};

export default RolePermissionsStep;
